import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { List, Heart, ChatCircle, GearSix, SignOut, Star, Gauge, UserCircle } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { ExpirationNudge } from './ExpirationNudge';

export const UserMenuPanel = ({ onClose, variant = 'dropdown' }) => {
  const { user, logout, isStaff } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    onClose?.();
    navigate('/');
  };

  const handleNav = (path) => {
    onClose?.();
    navigate(path);
  };

  if (!user) return null;

  const isMobile = variant === 'mobile';
  const wrapperClass = isMobile
    ? 'bg-white rounded-2xl shadow-lg border border-border p-3'
    : 'absolute right-0 mt-2 w-64 bg-white border border-border rounded-2xl shadow-lg py-2 z-50';

  const linkClass = isMobile
    ? 'flex items-center gap-3 py-2.5 px-3 rounded-lg text-text_main hover:bg-surface_muted font-medium'
    : 'flex items-center gap-3 py-2.5 px-4 text-text_main hover:bg-surface_muted font-medium mx-1 rounded-lg';

  const Divider = () => (
    <div className={`my-1.5 border-t border-border/60 ${isMobile ? 'mx-1' : 'mx-3'}`} role="separator" />
  );

  const iconSize = 18;

  return (
    <div className={wrapperClass} data-testid="user-menu-panel">
      {/* ─── Section 1 — Profile & content ─── */}
      <div data-testid="menu-section-profile">
        <Link to={`/users/${user.user_id}`} className={linkClass} onClick={() => handleNav(`/users/${user.user_id}`)} data-testid="menu-profile">
          <UserCircle size={iconSize} className="text-text_muted" /> {t('nav.profile')}
        </Link>
        <Link to="/my-listings" className={linkClass} onClick={() => handleNav('/my-listings')} data-testid="menu-my-listings">
          <List size={iconSize} className="text-text_muted" /> {t('nav.myListings')}
        </Link>
        <Link to="/favorites" className={linkClass} onClick={() => handleNav('/favorites')} data-testid="menu-favorites">
          <Heart size={iconSize} className="text-text_muted" /> {t('nav.favorites')}
        </Link>
        <Link to="/saved-searches" className={linkClass} onClick={() => handleNav('/saved-searches')} data-testid="menu-saved-searches">
          <Star size={iconSize} className="text-text_muted" /> {t('savedSearches.title')}
        </Link>
      </div>

      <Divider />

      {/* ─── Section 2 — Communication & activity ─── */}
      <div data-testid="menu-section-communication">
        <Link to="/messages" className={linkClass} onClick={() => handleNav('/messages')} data-testid="menu-messages">
          <ChatCircle size={iconSize} className="text-text_muted" /> {t('nav.messages')}
        </Link>
        {/* Expired-listings nudge — moved here so it sits alongside the
            other activity-related items, immediately under Messages. */}
        <ExpirationNudge variant="inline" onNavigate={onClose} />
      </div>

      <Divider />

      {/* ─── Section 3 — Settings ─── */}
      <div data-testid="menu-section-settings">
        <Link to="/settings" className={linkClass} onClick={() => handleNav('/settings')} data-testid="menu-profile-settings">
          <GearSix size={iconSize} className="text-text_muted" /> {t('nav.profileSettings')}
        </Link>
        {isStaff && (
          <Link to="/admin" className={linkClass} onClick={() => handleNav('/admin')} data-testid="menu-admin">
            <Gauge size={iconSize} className="text-text_muted" /> {t('nav.admin')}
          </Link>
        )}
      </div>

      <Divider />

      {/* ─── Section 4 — Logout ─── */}
      {/* "Publier une annonce" CTA removed — already exposed via the
          primary header button, so keeping it here was redundant. */}
      <div data-testid="menu-section-actions">
        <button onClick={handleLogout} className={`${linkClass} text-danger w-full`} data-testid="menu-logout">
          <SignOut size={iconSize} /> {t('nav.logout')}
        </button>
      </div>
    </div>
  );
};
